/**
 * LEK Species Info - displays Local Ecological Knowledge for a species selected in the LEK data layer.
 * Visibility controlled by FormContext 'show-lek-species-info'; species UUID stored in 'lek-selected-species'.
 * Receives speciesLEKData via rightPanelInjections.
 */
import React from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import './LekSpeciesInfo.css';

const FORM_KEY_VISIBLE = 'show-lek-species-info';
const FORM_KEY_SPECIES = 'lek-selected-species';

interface LekSpeciesInfoData {
  speciesLEKData?: {
    common_name?: string;
    scientific_name?: string;
    image_url?: string;
    alignment?: number;
    commentary?: string;
    bottom_type?: string;
    depth?: string;
    tide?: string;
    night_day?: string;
    moon_phase?: string;
  } | null;
  speciesLEKLoading?: boolean;
}

export default function LekSpeciesInfo(props: RegistryComponentProps): React.ReactElement | null {
  const formContext = useFormContextOptional();
  const injectionData = getRegistryData<LekSpeciesInfoData>(props);

  const isVisible = (formContext?.values?.[FORM_KEY_VISIBLE] as boolean) ?? false;
  const selectedSpecies = formContext?.values?.[FORM_KEY_SPECIES] as string | number | null | undefined;
  const speciesLEKData = injectionData.speciesLEKData ?? null;
  const speciesLEKLoading = injectionData.speciesLEKLoading ?? false;

  // Hide entirely when not visible, or when visible but no data (loading or loaded)
  if (!isVisible || (!speciesLEKLoading && !speciesLEKData)) return null;

  return (
    <div className="lek-species-info insight-card">
      <h3 className="lek-species-info-title">Local Ecological Knowledge</h3>

      {speciesLEKLoading && (
        <p className="lek-species-info-loading">
          Loading LEK data{selectedSpecies ? '...' : ''}
        </p>
      )}

      {!speciesLEKLoading && speciesLEKData && (
        <div className="lek-species-info-content">
          <div className="lek-species-info-header">
            <p className="lek-species-info-common-name">{speciesLEKData.common_name || 'N/A'}</p>
            <h4 className="lek-species-info-scientific">{speciesLEKData.scientific_name || 'Unknown Species'}</h4>
          </div>

          {speciesLEKData.image_url && (
            <div className="lek-species-info-image">
              <img
                src={speciesLEKData.image_url}
                alt={speciesLEKData.scientific_name || 'Species'}
              />
            </div>
          )}

          <div className="lek-species-info-grid">
            {speciesLEKData.alignment != null && (
              <div className="lek-species-info-card lek-species-info-alignment">
                <div className="lek-species-info-card-title">
                  Industry Degree of Agreement/ Disagreement with Assessment
                </div>
                <div className="lek-species-info-alignment-value">{speciesLEKData.alignment}/10</div>
                <div className={`lek-species-info-alignment-label alignment-${speciesLEKData.alignment}`}>
                  {speciesLEKData.alignment === 10 && 'Highest Agreement'}
                  {speciesLEKData.alignment >= 8 && speciesLEKData.alignment < 10 && 'High Agreement'}
                  {speciesLEKData.alignment >= 4 && speciesLEKData.alignment < 8 && 'Moderate Agreement'}
                  {speciesLEKData.alignment >= 2 && speciesLEKData.alignment < 4 && 'Low Agreement'}
                  {speciesLEKData.alignment === 1 && 'Lowest Agreement'}
                </div>
                {speciesLEKData.commentary && (
                  <p className="lek-species-info-commentary">{speciesLEKData.commentary}</p>
                )}
              </div>
            )}

            {speciesLEKData.bottom_type && (
              <div className="lek-species-info-card lek-species-info-bottom-type">
                <div className="lek-species-info-card-title">Bottom Type</div>
                <div className="lek-species-info-card-value">{speciesLEKData.bottom_type}</div>
              </div>
            )}

            {speciesLEKData.depth && (
              <div className="lek-species-info-card lek-species-info-depth">
                <div className="lek-species-info-card-title">Depth</div>
                <div className="lek-species-info-card-value">{speciesLEKData.depth}</div>
              </div>
            )}

            {speciesLEKData.tide && (
              <div className="lek-species-info-card lek-species-info-tide">
                <div className="lek-species-info-card-title">Tide Conditions</div>
                <div className="lek-species-info-card-value">{speciesLEKData.tide}</div>
              </div>
            )}

            {speciesLEKData.night_day && (
              <div className="lek-species-info-card lek-species-info-night-day">
                <div className="lek-species-info-card-title">Time of Day</div>
                <div className="lek-species-info-card-value">{speciesLEKData.night_day}</div>
              </div>
            )}

            {speciesLEKData.moon_phase && (
              <div className="lek-species-info-card lek-species-info-moon-phase">
                <div className="lek-species-info-card-title">Moon Phase</div>
                <div className="lek-species-info-card-value">{speciesLEKData.moon_phase}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
